import { useState, useEffect } from "react";
import { Eye, EyeOff, Lock, CheckCircle, XCircle, AlertCircle, KeyRound, Sparkles, Loader2 } from "lucide-react";
import apiClient from "../ApiClient/ApiClient";

const ChangePassword = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
  });

  // Calculate password strength
  const calculatePasswordStrength = (password) => {
    if (!password) {
      return { score: 0, feedback: [] };
    }

    let score = 0;
    const feedback = [];

    // Length check
    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push("Minimum 8 characters");
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Use lowercase letters");
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Use uppercase letters");
    }

    // Number check
    if (/\d/.test(password)) {
      score += 1;
    } else {
      feedback.push("Use numbers");
    }

    // Special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Use special characters");
    }

    return { score, feedback };
  };

  useEffect(() => {
    if (formData.newPassword) {
      const strength = calculatePasswordStrength(formData.newPassword);
      setPasswordStrength(strength);
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [formData.newPassword]);

  // Auto-hide error after 5 seconds
  useEffect(() => {
    if (error) {
      setShowError(true);
      const timer = setTimeout(() => {
        setShowError(false);
        setTimeout(() => {
          setError("");
        }, 300);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError("");
    setSuccess(false);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const getStrengthColor = (score) => {
    if (score === 0) return "bg-gray-500";
    if (score <= 2) return "bg-red-500";
    if (score <= 3) return "bg-yellow-500";
    if (score <= 4) return "bg-blue-500";
    return "bg-green-500";
  };

  const getStrengthLabel = (score) => {
    if (score === 0) return "Empty";
    if (score <= 2) return "Weak";
    if (score <= 3) return "Medium";
    if (score <= 4) return "Strong";
    return "Very Strong";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setShowError(false);

    // Validation
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError("All fields must be filled");
      return;
    }

    if (formData.newPassword.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }

    if (formData.newPassword === formData.currentPassword) {
      setError("New password must be different from current password");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("New password and confirm password do not match");
      return;
    }

    if (passwordStrength.score < 3) {
      setError("Password is too weak. Please make sure the password meets security criteria");
      return;
    }

    setIsLoading(true);

    try {
      // const token = localStorage.getItem("token");
      // if (!token) {
      //   setError("Session expired. Please login again");
      //   setIsLoading(false);
      //   return;
      // }

      let data = new FormData();
      data.append('old_password', formData.currentPassword);
      data.append('new_password', formData.newPassword);

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/user/password`,
        // headers: {
        //   Authorization: `Bearer ${token}`,
        // },
        data: data,
      };

      await apiClient.request(config);

      setSuccess(true);
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordStrength({ score: 0, feedback: [] });

      // Close modal after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Change password error:", error);
      
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data;

        if (status === 401) {
          setError("Current password is incorrect");
        } else if (status === 422) {
          const message = errorData?.message || "Data is invalid";
          setError(typeof message === "string" ? message : "Data is invalid. Please check your input");
        } else if (status === 400) {
          setError(errorData?.message || "Invalid request");
        } else {
          setError("Failed to change password. Please try again");
        }
      } else if (error.request) {
        setError("Cannot connect to server. Please check your internet connection");
      } else {
        setError("An error occurred. Please try again");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "520px",
          height: "auto",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <button
          className="modal-close"
          onClick={onClose}
          style={{ color: "#f0f2f5", fontSize: "28px", fontWeight: "300" }}
        >
          ×
        </button>

        <div className="flex flex-col gap-6 py-2">
          {/* Header */}
          <div className="text-center space-y-3">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 mb-2 glow-primary transition-transform duration-300">
              <KeyRound className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Change Password</h2>
            <p className="text-sm text-muted-foreground">
                Make sure your new password is strong and easy to remember.
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="rounded-md bg-green-500/15 border border-green-500/40 p-4 backdrop-blur-sm animate-slideDownFade">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-300">
                    Password changed successfully!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div
              className={`rounded-md bg-red-500/15 border border-red-500/40 p-4 backdrop-blur-sm transition-all duration-300 ease-in-out ${
                showError
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 -translate-y-2 pointer-events-none"
              }`}
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-300 font-medium flex-1">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Current Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Current Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground transition-all duration-300" />
                <input
                  name="currentPassword"
                  type={showPasswords.current ? "text" : "password"}
                  placeholder="Enter your current password"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 pr-12 transition-all duration-300"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("current")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-all duration-300 hover:scale-110"
                  disabled={isLoading}
                >
                  {showPasswords.current ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <KeyRound className="w-4 h-4" />
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground transition-all duration-300" />
                <input
                  name="newPassword"
                  type={showPasswords.new ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 pr-12 transition-all duration-300"
                  required
                  minLength={8}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("new")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-all duration-300 hover:scale-110"
                  disabled={isLoading}
                >
                  {showPasswords.new ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {formData.newPassword && (
                <div className="space-y-2 mt-3 animate-slideDownFade">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Password Strength:</span>
                    <span
                      className={`font-medium ${
                        passwordStrength.score <= 2
                          ? "text-red-400"
                          : passwordStrength.score <= 3
                          ? "text-yellow-400"
                          : passwordStrength.score <= 4
                          ? "text-blue-400"
                          : "text-green-400"
                      }`}
                    >
                      {getStrengthLabel(passwordStrength.score)}
                    </span>
                  </div>
                  <div className="flex gap-1 h-1.5 bg-black/30 rounded-full overflow-hidden">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`flex-1 transition-all duration-500 ${
                          level <= passwordStrength.score
                            ? getStrengthColor(passwordStrength.score)
                            : "bg-gray-600/30"
                        }`}
                        style={{
                          transform: level <= passwordStrength.score ? "scaleY(1)" : "scaleY(0.5)",
                        }}
                      />
                    ))}
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {passwordStrength.feedback.map((item, index) => (
                        <span
                          key={index}
                          className="text-xs px-2 py-1 rounded-md bg-red-500/10 border border-red-500/20 text-red-300"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground transition-all duration-300" />
                <input
                  name="confirmPassword"
                  type={showPasswords.confirm ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`flex h-11 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 pr-12 transition-all duration-300 ${
                    formData.confirmPassword &&
                    formData.newPassword &&
                    formData.confirmPassword !== formData.newPassword
                      ? "border-red-500/50 focus-visible:ring-red-500"
                      : formData.confirmPassword &&
                        formData.newPassword &&
                        formData.confirmPassword === formData.newPassword
                      ? "border-green-500/50 focus-visible:ring-green-500"
                      : "border-input"
                  }`}
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("confirm")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-all duration-300 hover:scale-110"
                  disabled={isLoading}
                >
                  {showPasswords.confirm ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
                {formData.confirmPassword &&
                  formData.newPassword &&
                  (formData.confirmPassword === formData.newPassword ? (
                    <CheckCircle className="absolute right-12 top-1/2 -translate-y-1/2 w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="absolute right-12 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
                  ))}
              </div>
              {formData.confirmPassword &&
                formData.newPassword &&
                formData.confirmPassword !== formData.newPassword && (
                  <p className="text-xs text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Password does not match
                  </p>
                )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-[#2b2b2b] hover:bg-[#171919] text-white font-medium shadow-lg hover:shadow-xl active:scale-[0.98] touch-manipulation select-none py-3 h-12 rounded-lg px-4 border border-[#656C7B]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || success}
                className="flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground font-semibold shadow-lg glow-primary hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.95] active:shadow-md touch-manipulation select-none py-3 h-12 rounded-lg px-4 relative overflow-hidden"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Changing...</span>
                  </>
                ) : success ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Success!</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    <span>Change Password</span>
                  </>
                )}
                {!isLoading && !success && (
                  <span className="absolute inset-0 rounded-lg bg-white/0 active:bg-white/10 transition-colors duration-150 pointer-events-none" />
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
